import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  runParallelEpisodeUpload,
  type WebEpisodeUploadProgress,
} from '@/lib/parallelEpisodeUpload'
import { listCreatorSeries, type CreatorSeriesSummary } from '@/lib/api/creator'

type UploadStatus = 'queued' | 'uploading' | 'processing' | 'ready' | 'failed'

interface UploadItem {
  id: string
  filename: string
  sizeMb: number
  progress: number
  status: UploadStatus
  file: File
}

export function CreatorUpload() {
  const [files, setFiles] = useState<UploadItem[]>([])
  const [seriesId, setSeriesId] = useState('')
  const [episodeTitle, setEpisodeTitle] = useState('')
  const [description, setDescription] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const [stage, setStage] = useState('')
  const [searchParams] = useSearchParams()
  const [seriesOptions, setSeriesOptions] = useState<CreatorSeriesSummary[]>([])
  const [loadingSeries, setLoadingSeries] = useState(false)

  useEffect(() => {
    const initialId = searchParams.get('seriesId')
    if (initialId) {
      setSeriesId(initialId)
    }
  }, [searchParams])

  useEffect(() => {
    let cancelled = false
    setLoadingSeries(true)
    listCreatorSeries()
      .then((items) => {
        if (cancelled) return
        setSeriesOptions(items)
      })
      .catch(() => {
        if (!cancelled) setSeriesOptions([])
      })
      .finally(() => {
        if (!cancelled) setLoadingSeries(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const onSelectFiles = (fileList: FileList | null) => {
    if (!fileList) return
    const next: UploadItem[] = Array.from(fileList).map((file, index) => ({
      id: `${Date.now()}-${index}`,
      filename: file.name,
      sizeMb: Math.round((file.size / (1024 * 1024)) * 10) / 10,
      progress: 0,
      status: 'queued',
      file,
    }))
    setFiles((prev) => [...next, ...prev])
  }

  const clearCompleted = () => {
    setFiles((prev) => prev.filter((item) => item.status !== 'ready' && item.status !== 'failed'))
  }

  const handleStartUpload = async () => {
    if (!seriesId.trim() || !episodeTitle.trim() || files.length === 0) {
      return
    }

    const jobs = files.map((file, index) => ({
      id: file.id,
      file: file.file,
      seriesId: seriesId.trim(),
      episodeNumber: index + 1,
      title: episodeTitle.trim(),
      description: description.trim(),
    }))

    setIsUploading(true)
    setStage('Preparing uploads…')
    setFiles((prev) =>
      prev.map((item) => ({
        ...item,
        status: 'uploading' as UploadStatus,
        progress: 0,
      })),
    )

    const controller = runParallelEpisodeUpload({
      jobs,
      onStageChange: (s) => setStage(s),
      onEpisodeProgress: (progress: WebEpisodeUploadProgress[]) => {
        setFiles((current) =>
          current.map((item) => {
            const ep = progress.find((p) => p.episodeId === item.id)
            if (!ep) return item
            let status: UploadStatus = item.status
            if (ep.status === 'queued') status = 'queued'
            else if (ep.status === 'thumbnail' || ep.status === 'video' || ep.status === 'saving') status = 'uploading'
            else if (ep.status === 'done') status = 'ready'
            else if (ep.status === 'error') status = 'failed'
            return {
              ...item,
              status,
              progress: ep.videoPct ?? item.progress,
            }
          }),
        )
      },
    })

    try {
      await controller.promise
      setStage('All uploads complete')
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      setStage(message)
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Upload episodes</h1>
          <p className="mt-1 text-sm text-gray-400">
            Upload new vertical episodes from your desktop. This screen simulates upload and
            processing locally until it&apos;s wired to the Binyuma backend.
          </p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1.6fr)]">
        <div className="rounded-xl border border-gray-800 bg-black/40 p-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Series
            </label>
            <select
              className="w-full rounded-lg border border-gray-800 bg-black/40 px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
              value={seriesId}
              onChange={(e) => setSeriesId(e.target.value)}
            >
              <option value="">{loadingSeries ? 'Loading series…' : 'Select a series'}</option>
              {seriesOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.title}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-gray-500">
              Choose an existing series to attach these episodes to.
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Episode title
            </label>
            <input
              type="text"
              className="w-full rounded-lg border border-gray-800 bg-black/40 px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
              placeholder="Episode title (used for all selected files in this mock)"
              value={episodeTitle}
              onChange={(e) => setEpisodeTitle(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Description
            </label>
            <textarea
              rows={3}
              className="w-full rounded-lg border border-gray-800 bg-black/40 px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
              placeholder="Optional description for your episode(s)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>

        <div className="rounded-xl border border-dashed border-gray-700 bg-black/40 p-4 flex flex-col items-center justify-center text-center">
          <input
            id="creator-upload-input"
            type="file"
            accept="video/*"
            multiple
            className="hidden"
            onChange={(e) => onSelectFiles(e.target.files)}
          />
          <label
            htmlFor="creator-upload-input"
            className="flex flex-col items-center gap-3 cursor-pointer"
          >
            <div className="w-16 h-16 rounded-full border border-gray-700 flex items-center justify-center bg-white/5">
              <span className="text-3xl">⬆️</span>
            </div>
            <div>
              <p className="text-sm font-medium text-white">Drop video files here</p>
              <p className="text-xs text-gray-400">
                or click to browse. Vertical MP4/WEBM recommended.
              </p>
            </div>
          </label>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-gray-400">
          {stage && <span>{stage}</span>}
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-black"
            disabled={isUploading || !seriesId.trim() || !episodeTitle.trim() || files.length === 0}
            onClick={handleStartUpload}
          >
            {isUploading ? 'Uploading…' : 'Start upload'}
          </Button>
          {files.some((f) => f.status === 'ready' || f.status === 'failed') && (
            <button
              type="button"
              onClick={clearCompleted}
              className="text-[11px] text-gray-400 hover:text-white"
            >
              Clear completed
            </button>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-gray-800 bg-black/40">
        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-800">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Upload queue
          </p>
        </div>
        {files.length === 0 ? (
          <div className="px-4 py-6 text-sm text-gray-400">
            No files in the queue yet. Add videos using the uploader above.
          </div>
        ) : (
          <div className="divide-y divide-gray-900">
            {files.map((file) => (
              <div key={file.id} className="px-4 py-3 flex items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-white truncate">{file.filename}</p>
                  <p className="text-[11px] text-gray-500">
                    {file.sizeMb} MB •{' '}
                    <span className="capitalize">
                      {file.status === 'queued'
                        ? 'waiting to upload'
                        : file.status === 'uploading'
                        ? 'uploading'
                        : file.status === 'ready'
                        ? 'ready'
                        : file.status === 'failed'
                        ? 'failed'
                        : file.status}
                    </span>
                  </p>
                </div>
                <div className="w-40 flex items-center gap-2">
                  <div className="flex-1 h-1.5 rounded-full bg-gray-800 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${
                        file.status === 'failed'
                          ? 'bg-red-500'
                          : file.status === 'ready'
                          ? 'bg-emerald-500'
                          : 'bg-[#D4AF37]'
                      }`}
                      style={{ width: `${file.progress}%` }}
                    />
                  </div>
                  <span className="text-[11px] text-gray-300 w-10 text-right">
                    {Math.round(file.progress)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-[11px] text-gray-500">
        This page now uses the same parallel episode upload pipeline as the mobile app, calling
        Binyuma&apos;s storage and creator APIs to create episodes for an existing series.
      </p>
    </div>
  )
}

